import * as React from "react";

import { cx } from "./controlStyles";

// The canonical surface as classes (the same material as the glassPanel /
// glassTile / glassInset presets): theme fill, hairline edge, top bevel,
// the theme's lift and blur. Flat themes (Earth, Midnight) resolve the
// same classes to opaque fills with no blur or lift; the hairline edge
// stays, so a card shows where the ground is as light as its fill.
// (shadow-card-* are named in tailwind.config: an arbitrary shadow ending
// in var(--shadow-panel) compiled to nothing, so cards had no edge.)
//
//   <Card>
//     <CardHeader><CardTitle>Recent activity</CardTitle><Button …/></CardHeader>
//     <CardContent>…</CardContent>
//     <CardFooter>…</CardFooter>
//   </Card>

const VARIANT = {
  panel:
    "rounded-card bg-panel [backdrop-filter:var(--blur-lg)] shadow-card-panel",
  tile:
    "rounded-card bg-[color:var(--c-glass-tile)] [backdrop-filter:var(--blur-lg)] shadow-card-tile",
  inset:
    "rounded-lg bg-inset shadow-[inset_0_1px_2px_var(--c-bevel-bottom-dark),inset_0_0_0_1px_var(--c-border-hairline)]",
} as const;

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: keyof typeof VARIANT;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = "panel", ...props }, ref) => (
    <div ref={ref} className={cx("min-w-0", VARIANT[variant], className)} {...props} />
  ),
);
Card.displayName = "Card";

/** Card head: title left, actions right, 52px min, hairline under. */
export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cx(
      "flex min-h-[52px] items-center justify-between gap-3 border-b border-line-subtle px-4 py-3",
      className,
    )}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

/** Card title: 16px semibold, the same as SectionHeader (one section-title size). */
export const CardTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement> & { as?: "h2" | "h3" | "h4" }
>(({ className, as: Tag = "h3", ...props }, ref) => (
  <Tag
    ref={ref}
    className={cx("min-w-0 truncate text-md font-semibold text-fg", className)}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cx("text-sm text-fg-muted", className)} {...props} />
));
CardDescription.displayName = "CardDescription";

export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cx("p-4", className)} {...props} />
));
CardContent.displayName = "CardContent";

export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cx(
      "flex items-center gap-3 border-t border-line-subtle px-4 py-3",
      className,
    )}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";
